/**
 * Módulo para ordenar los campos de un JSON de Comprobante Fiscal Electrónico (e-CF)
 * según el orden definido en el esquema XSD correspondiente
 */

/**
 * Ordena los campos de un objeto JSON según el orden definido para el tipo de e-CF
 * @param {Object} ecfData - Datos del e-CF en formato JSON
 * @returns {Object} - Datos del e-CF ordenados según el esquema XSD
 */
function orderECFByXSD(ecfData) {
  // Verificar si los datos son válidos
  if (!ecfData || !ecfData.ECF || !ecfData.ECF.Encabezado || !ecfData.ECF.Encabezado.IdDoc) {
    console.error('Los datos del e-CF no son válidos para ordenar');
    return ecfData;
  }

  // Obtener el tipo de e-CF
  const tipoECF = ecfData.ECF.Encabezado.IdDoc.TipoeCF;

  // Ordenar según el tipo de e-CF
  switch (tipoECF) {
    case "31":
      return orderECF31(ecfData);
    case "32":
      return orderECF32(ecfData);
    case "33":
      // Implementar ordenamiento para tipo 33 si es necesario
      return ecfData;
    case "34":
      // Implementar ordenamiento para tipo 34 si es necesario
      return ecfData;
    default:
      // Si no hay ordenamiento específico, devolver los datos sin cambios
      return ecfData;
  }
}

/**
 * Ordena los campos de un e-CF tipo 31 (Factura de Crédito Fiscal Electrónica)
 * según el orden definido en el esquema XSD
 * @param {Object} ecfData - Datos del e-CF tipo 31
 * @returns {Object} - Datos ordenados según el esquema XSD
 */
function orderECF31(ecfData) {
  // Crear una copia profunda de los datos para no modificar el original
  const data = JSON.parse(JSON.stringify(ecfData));
  
  // Estructura ordenada para ECF tipo 31
  const orderedECF = {
    ECF: {
      Encabezado: {
        Version: data.ECF.Encabezado.Version,
        IdDoc: orderIdDoc31(data.ECF.Encabezado.IdDoc),
        Emisor: orderEmisor31(data.ECF.Encabezado.Emisor),
        Comprador: orderComprador31(data.ECF.Encabezado.Comprador),
        Totales: orderTotales31(data.ECF.Encabezado.Totales)
      },
      DetallesItems: {
        Item: orderItems31(data.ECF.DetallesItems.Item)
      },
      FechaHoraFirma: data.ECF.FechaHoraFirma
    }
  };

  // Devolver la estructura ordenada
  return orderedECF;
}

/**
 * Ordena los campos de la sección IdDoc para e-CF tipo 31
 * @param {Object} idDoc - Datos de la sección IdDoc
 * @returns {Object} - Datos ordenados
 */
function orderIdDoc31(idDoc) {
  return {
    TipoeCF: idDoc.TipoeCF,
    eNCF: idDoc.eNCF,
    FechaVencimientoSecuencia: idDoc.FechaVencimientoSecuencia,
    IndicadorMontoGravado: idDoc.IndicadorMontoGravado,
    TipoIngresos: idDoc.TipoIngresos,
    TipoPago: idDoc.TipoPago,
    ...(idDoc.TablaFormasPago && { TablaFormasPago: idDoc.TablaFormasPago }),
    ...(idDoc.FechaLimitePago && { FechaLimitePago: idDoc.FechaLimitePago }),
    ...(idDoc.TerminoPago && { TerminoPago: idDoc.TerminoPago })
  };
}

/**
 * Ordena los campos de la sección Emisor para e-CF tipo 31
 * @param {Object} emisor - Datos de la sección Emisor
 * @returns {Object} - Datos ordenados
 */
function orderEmisor31(emisor) {
  return {
    RNCEmisor: emisor.RNCEmisor,
    RazonSocialEmisor: emisor.RazonSocialEmisor,
    NombreComercial: emisor.NombreComercial,
    DireccionEmisor: emisor.DireccionEmisor,
    FechaEmision: emisor.FechaEmision
  };
}

/**
 * Ordena los campos de la sección Comprador para e-CF tipo 31
 * @param {Object} comprador - Datos de la sección Comprador
 * @returns {Object} - Datos ordenados
 */
function orderComprador31(comprador) {
  return {
    RNCComprador: comprador.RNCComprador,
    RazonSocialComprador: comprador.RazonSocialComprador,
    ...(comprador.DireccionComprador && { DireccionComprador: comprador.DireccionComprador }),
    ...(comprador.TelefonoAdicional && { TelefonoAdicional: comprador.TelefonoAdicional })
  };
}

/**
 * Ordena los campos de la sección Totales para e-CF tipo 31
 * @param {Object} totales - Datos de la sección Totales
 * @returns {Object} - Datos ordenados
 */
function orderTotales31(totales) {
  // Estructura correcta según el esquema XSD oficial
  return {
    MontoNoFacturable: totales.MontoNoFacturable,
    MontoPeriodo: totales.MontoPeriodo,
    SaldoAnterior: totales.SaldoAnterior,
    MontoAvancePago: totales.MontoAvancePago,
    ValorPagar: totales.ValorPagar || totales.MontoTotal,
    TotalITBISRetenido: totales.TotalITBISRetenido,
    TotalISRRetencion: totales.TotalISRRetencion,
    TotalITBISPercepcion: totales.TotalITBISPercepcion,
    TotalISRPercepcion: totales.TotalISRPercepcion
  };
}

/**
 * Ordena los campos de los ítems para e-CF tipo 31
 * @param {Array} items - Array de ítems
 * @returns {Array} - Array de ítems ordenados
 */
function orderItems31(items) {
  if (!Array.isArray(items)) return [];
  
  return items.map(item => ({
    NumeroLinea: item.NumeroLinea,
    IndicadorFacturacion: item.IndicadorFacturacion,
    NombreItem: item.NombreItem,
    IndicadorBienoServicio: item.IndicadorBienoServicio,
    CantidadItem: item.CantidadItem,
    PrecioUnitarioItem: item.PrecioUnitarioItem,
    MontoItem: item.MontoItem,
    ...(item.UnidadMedida && { UnidadMedida: item.UnidadMedida })
  }));
}

// Exportar la función principal
export default orderECFByXSD;